<template>
  <div :id="data.id" class="bg-gray-lighten3 py-10 md:py-14">
    <div class="container">
      <UiSectionTitle
        v-if="data.title"
        :title="data.title"
        class="title mb-8 text-center text-xl uppercase leading-none text-slate md:mb-12 md:whitespace-pre-line md:text-5xl"
      />

      <div
        v-if="data.plans && data.plans.length"
        class="mb-8 flex flex-col justify-center gap-y-6 md:mb-12 md:flex-row md:gap-x-4"
      >
        <div
          v-for="(plan, i) in data.plans"
          :key="i"
          class="flex w-full max-w-full flex-col overflow-hidden rounded-lg shadow-[0_4px_74px_0px_rgba(0,0,0,0.04)] md:w-[437px]"
        >
          <div
            v-if="plan.header"
            class="flex flex-shrink-0 flex-col items-center bg-gradient-to-b px-6 py-8 text-white md:p-12"
            :class="getPlanHeaderBg(i)"
          >
            <div
              v-if="plan.header.image"
              v-lazy-background="getAssetUrl(plan.header.image)"
              class="mb-6 h-20 w-20 bg-contain bg-no-repeat"
            ></div>

            <div
              v-if="plan.header.title"
              class="title mb-2 text-lg uppercase leading-none"
              v-html="plan.header.title"
            />

            <div
              v-if="plan.header.text"
              class="whitespace-pre-line text-center text-sm leading-[1.225rem]"
              v-html="plan.header.text"
            />
          </div>

          <div
            v-if="plan.body"
            class="flex flex-grow flex-col items-center bg-white px-6 py-8 text-center text-slate md:p-12"
          >
            <div
              v-if="plan.body.title"
              class="title mb-2 text-xl uppercase leading-none"
              v-html="plan.body.title"
            />

            <div
              v-if="plan.body.text"
              class="mb-6 text-md leading-[1.8rem] md:whitespace-pre-line"
              v-html="plan.body.text"
            />

            <div class="mt-auto w-full">
              <UiButton
                v-if="plan.body.button"
                :to="plan.body.button.link"
                :download="plan.body.button.download"
                :target="plan.body.button.target"
                :modal="plan.body.button.modal"
                :min-width="isDesktop ? '211px' : '280px'"
                :variant="i % 2 === 0 ? 'primary' : 'secondary'"
                size="md"
              >
                <span v-html="plan.body.button.text" />
              </UiButton>
            </div>
          </div>
        </div>
      </div>

      <div v-if="data.footer" class="flex flex-col items-center">
        <div
          v-if="data.footer.text"
          class="mb-4 text-center text-sm leading-[1.225rem] text-slate"
          v-html="data.footer.text"
        />

        <div
          v-if="data.footer.logos && data.footer.logos.length"
          class="flex gap-6"
        >
          <CdnImg
            v-for="(logo, i) in data.footer.logos"
            :key="i"
            :src="handleImageSrc(logo)"
            :alt="handleImageAlt(logo)"
            class="w-auto max-w-full"
            loading="lazy"
            :height="27"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CdnImg } from '@webplatform/cdn-image';
import { useBreakpoint, useMediaAsset } from '~/composables';
import { handleImageSrc, handleImageAlt } from '~/helpers';

interface Props {
  data?: any;
}

withDefaults(defineProps<Props>(), {
  data: () => ({}),
});

const gradients = [
  'from-[#432A9E] to-[#2050A7]',
  'from-[#635E78] to-[#485876]',
];

const { getAssetUrl } = useMediaAsset();
const { isBiggerThanBreakpoint: isDesktop } = useBreakpoint('md');

const getPlanHeaderBg = (index: number) => gradients[index];
</script>
